import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  InputBase,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect, useContext } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useDeals } from "../../hooks/useDealList";
import { useFollowingDeals } from "../../hooks/useFollowingDeals";
import { useAuth } from "../../contexts/AuthContext";
import CustomButton from "../ui/CustomButton";
import {
  DealListRow,
  DealListRowSkeleton,
} from "./dealList/components/DealListRow";
import DealListLoadMoreButton from "./dealList/components/DealListLoadMoreButton";
import FilterEmptyHint from "./dealList/components/FilterEmptyHint";
import DealsFilterIconButton from "./dealList/components/DealsFilterIconButton";
import {
  DEAL_LIST_BOTTOM_SCROLL_RESERVE,
  DEAL_LIST_SECTION_PAGE_SIZE,
} from "./dealList/constants";
import {
  getAvailableDayChips,
  isDealAvailableOnDay,
  sortDealsByValidityPriority,
} from "./dealList/utils/calendarDealBuckets";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { countAppliedDealsFilters } from "../../utils/countAppliedDealsFilters";
import { TabsContext } from "./dealsTabsContext";

const EMPTY_FOLLOWING_FILTERS = {
  timeOfDay: "",
  date: "",
  area: "",
  category: "",
  discountType: "",
  priceBand: "",
};

const mergeDealsById = (lists) => {
  const byId = new Map();
  for (const list of lists) {
    for (const deal of list) {
      if (deal?._id) byId.set(deal._id, deal);
    }
  }
  return [...byId.values()];
};

const TodayWeekSearchBar = ({
  searchInput,
  onSearchChange,
  onClearSearch,
  surfaceNeutral,
  filterButton = null,
  placeholder = "Search deals",
  inputAriaLabel = "Search deals",
}) => {
  const showClear = Boolean(searchInput.trim());
  const trailingIconSx = {
    flexShrink: 0,
    p: 0.35,
    color: "#888",
    WebkitTapHighlightColor: "transparent",
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.85,
        px: 1.35,
        mb: 1,
        borderRadius: 2,
        bgcolor: surfaceNeutral.surface,
        border: "none",
        boxShadow: "none",
      }}
    >
      <SearchIcon sx={{ color: "#888", fontSize: 20, flexShrink: 0 }} />
      <InputBase
        placeholder={placeholder}
        value={searchInput}
        onChange={onSearchChange}
        autoFocus={false}
        inputProps={{
          inputMode: "search",
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: "false",
          "aria-label": inputAriaLabel,
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-input": {
            fontSize: "16px",
            lineHeight: 1.25,
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          gap: 0.25,
          ml: "auto",
        }}
      >
        <IconButton
          type="button"
          size="small"
          onClick={onClearSearch}
          aria-label={showClear ? "Clear search" : undefined}
          aria-hidden={!showClear}
          tabIndex={showClear ? 0 : -1}
          sx={{
            ...trailingIconSx,
            display: showClear ? "inline-flex" : "none",
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
        {filterButton}
      </Box>
    </Box>
  );
};

const DayChipPicker = ({ chips, selectedKey, onSelect }) => (
  <Box
    sx={{
      display: "flex",
      gap: 0.75,
      overflowX: "auto",
      mx: { xs: -2 },
      px: 2,
      pt: 1,
      pb: 0.5,
      "&::after": {
        content: '""',
        display: "block",
        flexShrink: 0,
        width: 8,
      },
      "&::-webkit-scrollbar": { display: "none" },
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    }}
  >
    {chips.map((chip) => {
      const isSelected = chip.dateKey === selectedKey;
      return (
        <Box
          key={chip.dateKey}
          onClick={() => onSelect(chip.dateKey)}
          sx={{
            flexShrink: 0,
            px: 1.75,
            py: 0.85,
            borderRadius: 999,
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            userSelect: "none",
            bgcolor: isSelected ? "#1a1a1a" : "#f3f2ed",
            color: isSelected ? "#fff" : "#444",
            fontWeight: isSelected ? 600 : 500,
            fontSize: "0.85rem",
            lineHeight: 1,
            transition: "background-color 0.15s ease, color 0.15s ease",
          }}
        >
          {chip.label}
        </Box>
      );
    })}
  </Box>
);

const listShellSx = {
  maxWidth: 720,
  mx: "auto",
  px: { xs: 2 },
  pt: { xs: 0.5, md: 0 },
  pb: DEAL_LIST_BOTTOM_SCROLL_RESERVE,
};

const DealsTodayWeekTab = ({
  isActive,
  onExpandDeal,
  followingOnly = false,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const n = theme.palette.neutral || uiNeutrals;
  const tabs = useContext(TabsContext);
  const followingFromContext =
    tabs?.followingFilters ?? EMPTY_FOLLOWING_FILTERS;
  const openFilters = tabs?.setOpenFilterDrawer;

  const [searchInput, setSearchInput] = useState("");
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [dealLimit, setDealLimit] = useState(DEAL_LIST_SECTION_PAGE_SIZE);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setSearchInput(p.get("search") || "");
  }, [location.search]);

  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      const p = new URLSearchParams(location.search);
      const next = searchInput.trim();
      const cur = (p.get("search") || "").trim();
      if (next === cur) return;
      if (next) p.set("search", next);
      else p.delete("search");
      navigate(`${location.pathname}?${p.toString()}`, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, isActive, location.pathname, location.search, navigate]);

  const filters = useMemo(() => {
    const p = new URLSearchParams(location.search);
    if (followingOnly) {
      return {
        area: p.get("area") || "",
        date: p.get("date") || "",
        timeOfDay: p.get("time_of_day") || "",
        category: p.get("category") || "",
        discountType: p.get("discount_type") || "",
        priceBand: p.get("price_band") || "",
        search: p.get("search") || "",
      };
    }
    const pick = (paramKey, ctxVal) =>
      p.has(paramKey) ? p.get(paramKey) || "" : ctxVal || "";
    return {
      area: pick("area", followingFromContext.area),
      date: pick("date", followingFromContext.date),
      timeOfDay: pick("time_of_day", followingFromContext.timeOfDay),
      category: pick("category", followingFromContext.category),
      discountType: pick("discount_type", followingFromContext.discountType),
      priceBand: pick("price_band", followingFromContext.priceBand),
      search: p.get("search") || "",
    };
  }, [location.search, followingFromContext, followingOnly]);

  const appliedFilterCount = useMemo(
    () => countAppliedDealsFilters(filters),
    [filters],
  );

  const filterButton = openFilters ? (
    <DealsFilterIconButton
      count={appliedFilterCount}
      onClick={() => openFilters(true)}
    />
  ) : null;

  const trimmedSearch = (filters.search || "").trim();
  const isSearchResultsMode = trimmedSearch.length > 0;

  const baseFilters = useMemo(() => {
    if (!trimmedSearch) return filters;
    return {
      ...filters,
      area: "",
      date: "",
      timeOfDay: "",
      category: "",
      discountType: "",
      priceBand: "",
      search: trimmedSearch,
    };
  }, [filters, trimmedSearch]);

  const thisWeekFilters = useMemo(
    () => ({ ...baseFilters, date: "this_week" }),
    [baseFilters],
  );

  const nextWeekFilters = useMemo(
    () => ({ ...baseFilters, date: "next_week" }),
    [baseFilters],
  );

  const queryKey = useMemo(
    () => JSON.stringify({ mode: "available", filters: baseFilters }),
    [baseFilters],
  );

  useEffect(() => {
    setDealLimit(DEAL_LIST_SECTION_PAGE_SIZE);
  }, [queryKey]);

  const handleClearSearch = () => {
    setSearchInput("");
    const p = new URLSearchParams(location.search);
    p.delete("search");
    const qs = p.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
      replace: true,
    });
  };

  const queryOpts = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };

  const queryEnabled = isActive;

  const thisWeekBrowse = useDeals(thisWeekFilters, isMobile, {
    ...queryOpts,
    enabled: queryEnabled && !followingOnly,
  });

  const nextWeekBrowse = useDeals(nextWeekFilters, isMobile, {
    ...queryOpts,
    enabled: queryEnabled && !followingOnly,
  });

  const thisWeekFollowing = useFollowingDeals(thisWeekFilters, isMobile, {
    ...queryOpts,
    enabled: queryEnabled && followingOnly,
  });

  const nextWeekFollowing = useFollowingDeals(nextWeekFilters, isMobile, {
    ...queryOpts,
    enabled: queryEnabled && followingOnly,
  });

  const thisWeekQuery = followingOnly ? thisWeekFollowing : thisWeekBrowse;
  const nextWeekQuery = followingOnly ? nextWeekFollowing : nextWeekBrowse;

  const allDeals = useMemo(
    () => mergeDealsById([thisWeekQuery.deals, nextWeekQuery.deals]),
    [thisWeekQuery.deals, nextWeekQuery.deals],
  );

  const loading = thisWeekQuery.loading || nextWeekQuery.loading;
  const status =
    thisWeekQuery.status === "error" || nextWeekQuery.status === "error"
      ? "error"
      : thisWeekQuery.status;
  const loadingMore = thisWeekQuery.loadingMore || nextWeekQuery.loadingMore;
  const hasMore = thisWeekQuery.hasMore || nextWeekQuery.hasMore;

  const fetchDeals = (cursor, isLoadMore) => {
    if (thisWeekQuery.hasMore && !thisWeekQuery.loadingMore) {
      thisWeekQuery.fetchDeals(
        isLoadMore ? thisWeekQuery.nextCursor : cursor,
        isLoadMore,
      );
      return;
    }
    if (nextWeekQuery.hasMore && !nextWeekQuery.loadingMore) {
      nextWeekQuery.fetchDeals(
        isLoadMore ? nextWeekQuery.nextCursor : cursor,
        isLoadMore,
      );
    }
  };

  const dayChips = useMemo(() => getAvailableDayChips(), []);

  const effectiveDayKey =
    selectedDayKey && dayChips.some((c) => c.dateKey === selectedDayKey)
      ? selectedDayKey
      : ((dayChips.find((c) => c.isToday) ?? dayChips[0])?.dateKey ?? null);

  const selectedChip = dayChips.find((c) => c.dateKey === effectiveDayKey);

  const filteredDeals = useMemo(() => {
    if (isSearchResultsMode) return sortDealsByValidityPriority(allDeals);
    if (!selectedChip || !allDeals.length) return [];
    return sortDealsByValidityPriority(
      allDeals.filter((deal) => isDealAvailableOnDay(deal, selectedChip.date)),
    );
  }, [allDeals, selectedChip, isSearchResultsMode]);

  const handleLoadMore = (listLength) => {
    if (dealLimit < listLength) {
      setDealLimit((n) => n + DEAL_LIST_SECTION_PAGE_SIZE);
      return;
    }
    if (hasMore && !loadingMore) {
      fetchDeals(null, true);
    }
  };

  const loadingInitial = loading && !allDeals.length;

  const searchBar = (
    <TodayWeekSearchBar
      searchInput={searchInput}
      onSearchChange={(e) => setSearchInput(e.target.value)}
      onClearSearch={handleClearSearch}
      surfaceNeutral={n}
      filterButton={followingOnly ? null : filterButton}
      placeholder={
        followingOnly ? "Search deals from brands you follow" : "Search deals"
      }
      inputAriaLabel={
        followingOnly ? "Search deals from brands you follow" : "Search deals"
      }
    />
  );

  if (followingOnly && !isAuthenticated) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Box sx={{ py: 6, textAlign: "center", px: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Sign in to see following
          </Typography>
          <CustomButton
            variant="secondary"
            onClick={() => navigate("/login")}
            sx={{ minWidth: 120, mt: 2 }}
          >
            Login
          </CustomButton>
        </Box>
      </Box>
    );
  }

  if (isSearchResultsMode) {
    if (isActive && loadingInitial) {
      return (
        <Box sx={listShellSx}>
          {searchBar}
          {Array.from({ length: 8 }).map((_, i) => (
            <DealListRowSkeleton key={i} />
          ))}
        </Box>
      );
    }

    if (status === "error") {
      return (
        <Box sx={listShellSx}>
          {searchBar}
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            Could not load deals. Try again later.
          </Typography>
        </Box>
      );
    }

    if (!loading && allDeals.length === 0) {
      return (
        <Box sx={listShellSx}>
          {searchBar}
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            No deals match your search.
          </Typography>
          {!followingOnly ? (
            <FilterEmptyHint
              preamble="Try another search phrase, or change your filters."
              onOpenFilters={openFilters ? () => openFilters(true) : undefined}
            />
          ) : null}
        </Box>
      );
    }

    const visibleDeals = allDeals.slice(0, dealLimit);
    const showLoadMore =
      allDeals.length > dealLimit || (hasMore && allDeals.length >= dealLimit);

    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Box sx={{ mt: 1 }}>
          {visibleDeals.map((deal, index) => (
            <DealListRow
              key={deal._id}
              deal={deal}
              index={index}
              onExpandDeal={onExpandDeal}
            />
          ))}
        </Box>
        {loadingMore ? (
          <Box sx={{ mt: 1 }}>
            {Array.from({ length: 2 }).map((_, i) => (
              <DealListRowSkeleton key={`lm-${i}`} />
            ))}
          </Box>
        ) : null}
        {showLoadMore ? (
          <DealListLoadMoreButton
            onClick={() => handleLoadMore(allDeals.length)}
            loading={loadingMore}
          />
        ) : null}
      </Box>
    );
  }

  const chipOnSelect = (key) => {
    setSelectedDayKey(key);
    setDealLimit(DEAL_LIST_SECTION_PAGE_SIZE);
  };

  const chipPicker = (
    <DayChipPicker
      chips={dayChips}
      selectedKey={effectiveDayKey}
      onSelect={chipOnSelect}
    />
  );

  if (isActive && loadingInitial) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        {chipPicker}
        {Array.from({ length: 8 }).map((_, i) => (
          <DealListRowSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        {chipPicker}
        <Typography sx={{ color: "#666", fontSize: "0.9rem", mt: 1 }}>
          Could not load deals. Try again later.
        </Typography>
      </Box>
    );
  }

  const visibleDeals = filteredDeals.slice(0, dealLimit);
  const isEmpty = !loading && filteredDeals.length === 0;

  return (
    <Box sx={listShellSx}>
      {searchBar}
      {chipPicker}
      {isEmpty ? (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            {followingOnly
              ? "No deals from restaurants you follow on this day."
              : "No deals available on this day with these filters."}
          </Typography>
          {!followingOnly ? (
            <FilterEmptyHint
              preamble="Try a different day or change your filters."
              onOpenFilters={openFilters ? () => openFilters(true) : undefined}
            />
          ) : null}
        </Box>
      ) : (
        <Box sx={{ mt: 0.5 }}>
          {visibleDeals.map((deal, index) => (
            <DealListRow
              key={deal._id}
              deal={deal}
              index={index}
              onExpandDeal={onExpandDeal}
            />
          ))}
          {loadingMore ? (
            <Box sx={{ mt: 1 }}>
              {Array.from({ length: 2 }).map((_, i) => (
                <DealListRowSkeleton key={`lm-${i}`} />
              ))}
            </Box>
          ) : null}
          {filteredDeals.length > dealLimit || hasMore ? (
            <DealListLoadMoreButton
              onClick={() => handleLoadMore(filteredDeals.length)}
              loading={loadingMore}
            />
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default DealsTodayWeekTab;
